import { useEffect, useState } from 'react';
import { formatPrice } from '@cracked-mug/shared';
import { adminFetchAnalytics, type AnalyticsPayload } from '../../services/adminApi';
import { fieldClass } from '../../components/ui/fieldStyles';
import { cn } from '../../lib/cn';

export function AdminDashboard() {
  const [data, setData] = useState<AnalyticsPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    adminFetchAnalytics(windowDays)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl">Dashboard</h1>
        <div className="flex items-center gap-2">
          <label htmlFor="window" className="text-sm text-wca-gray-mid">
            Window:
          </label>
          <select
            id="window"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className={cn(fieldClass, 'w-auto py-2')}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </header>

      {error !== null && (
        <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
      )}

      {isLoading || !data ? (
        <p className="font-mono text-sm text-wca-gray-mid py-12">Loading analytics…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total orders" value={String(data.totalOrders)} />
            <StatCard label="Completed" value={String(data.completedOrders)} />
            <StatCard label="Revenue" value={formatPrice(data.totalRevenueCents)} />
            <StatCard label="Avg order" value={formatPrice(data.averageOrderCents)} />
          </div>

          <section className="rounded-2xl border border-wca-black/[0.06] bg-wca-surface p-5 shadow-card">
            <h2 className="font-display text-xl mb-3">Top items</h2>
            {data.topItems.length === 0 ? (
              <p className="text-sm text-wca-gray-mid">No orders yet.</p>
            ) : (
              <ol className="space-y-1">
                {data.topItems.map((item, idx) => (
                  <li
                    key={item.itemId}
                    className="flex items-center justify-between gap-3 py-2 border-b border-wca-gray-light last:border-b-0"
                  >
                    <span className="flex items-center gap-2.5 text-sm">
                      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-wca-black/[0.05] font-mono text-[11px] text-wca-gray-mid">
                        {idx + 1}
                      </span>
                      {item.name}
                    </span>
                    <span className="font-mono text-sm text-wca-red tabular-nums">
                      {item.quantity}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="rounded-2xl border border-wca-black/[0.06] bg-wca-surface p-5 shadow-card">
            <h2 className="font-display text-xl mb-3">Hourly breakdown</h2>
            <div
              className="grid grid-cols-12 sm:grid-cols-24 gap-1"
              style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}
            >
              {data.hourlyOrders.map((bucket) => {
                const max = Math.max(...data.hourlyOrders.map((b) => b.count), 1);
                const heightPct = (bucket.count / max) * 100;
                return (
                  <div
                    key={bucket.hour}
                    className="flex flex-col items-center gap-1"
                    title={`${String(bucket.hour)}:00 — ${String(bucket.count)} orders`}
                  >
                    <div className="relative h-24 w-full overflow-hidden rounded-md bg-wca-black/[0.04]">
                      <div
                        className="absolute bottom-0 left-0 right-0 rounded-t-md bg-gradient-to-t from-wca-red to-wca-red/65"
                        style={{ height: `${String(heightPct)}%` }}
                      />
                    </div>
                    <span className="font-mono text-[10px] text-wca-gray-mid">{bucket.hour}</span>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-wca-black/[0.06] bg-wca-surface p-5 shadow-card transition-all duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-card-lg">
      <p className="font-mono text-xs uppercase tracking-widest text-wca-gray-mid">{label}</p>
      <p className="mt-1 font-display text-3xl tabular-nums">{value}</p>
    </div>
  );
}
